import { z } from 'zod';
import { Result, TaggedError } from 'better-result';
import { Option } from 'effect';
import { parseJson } from '@finnest/core/json';
import { ValidationError } from '@finnest/core/result';
import { powerRefusalOf } from '@finnest/power-admin-contracts/failure';
import type { PowerRefusal } from '@finnest/power-admin-contracts/failure';

const problemDetailSchema = z.object({
  detail: z.string().min(1).exactOptional(),
  errors: z.array(z.object({ message: z.string().min(1) })).exactOptional(),
  title: z.string().min(1).exactOptional(),
});
type ProblemDetail = z.infer<typeof problemDetailSchema>;

// The operator reads this when the server answers without a problem body (a proxy error page).
export const failedCallMessage = (status: number): string =>
  `O servidor do onboarding não respondeu como esperado (HTTP ${status}).`;

const missingProblemMessage = (reason: string): ValidationError =>
  new ValidationError({ field: 'problem', message: reason });

const messageFromErrors = (problem: ProblemDetail): Result<string, ValidationError> => {
  const { errors } = problem;
  if (errors === undefined || errors.length === 0) {
    return Result.err(missingProblemMessage('problem+json had no error messages'));
  }
  const [firstError] = errors;
  if (firstError === undefined) {
    return Result.err(missingProblemMessage('problem+json had no error messages'));
  }
  return Result.ok(firstError.message);
};

const problemMessage = (problem: ProblemDetail): Result<string, ValidationError> => {
  const { detail, title } = problem;
  if (detail !== undefined) return Result.ok(detail);
  return messageFromErrors(problem).match({
    ok: (message) => Result.ok(message),
    err: () =>
      title === undefined
        ? Result.err(missingProblemMessage('problem+json had no message'))
        : Result.ok(title),
  });
};

const readProblemMessage = (response: Response, text: string): Result<string, ValidationError> => {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/problem+json')) {
    return Result.err(missingProblemMessage('response is not application/problem+json'));
  }
  return parseJson(text, problemDetailSchema).andThen(problemMessage);
};

const HTTP_NOT_FOUND = 404;

/**
 * Power refused an onboarding call: the Problem's words (or the status) as the message, the HTTP
 * status it answered with, and the error code and trace id its body names, which the operator
 * quotes to support.
 */
class OnboardingRequestRefused extends TaggedError('OnboardingRequestRefused')<{
  message: string;
  status: number;
  refusal: Option.Option<PowerRefusal>;
}> {}

export const rejectHttp = async (response: Response): Promise<never> => {
  // A body that cannot be read names no Problem: the refusal keeps the status message and no
  // reference, whatever stopped the read.
  const [read] = await Promise.allSettled([response.clone().text()]);
  const body = read.status === 'fulfilled' ? Option.some(read.value) : Option.none<string>();
  const message = Option.flatMap(body, (text) =>
    readProblemMessage(response, text).match({
      ok: (detail) => Option.some(detail),
      err: () => Option.none<string>(),
    }),
  ).pipe(Option.getOrElse(() => failedCallMessage(response.status)));
  const refusal = Option.flatMap(body, powerRefusalOf);
  return await Promise.reject(
    new OnboardingRequestRefused({ message, status: response.status, refusal }),
  );
};

/**
 * The error code and trace id a failed onboarding call names, to quote to support; none when Power
 * did not answer (a network failure) or answered without a code. React Query hands
 * the screens a query's failure as a plain `Error`, whatever the fetcher rejected with, so this
 * boundary narrows it at run time with `instanceof`: a cast would claim a refusal that a network
 * `TypeError` or a decode `Error` is not.
 */
export const onboardingFailureRefusal = (failure: Error): Option.Option<PowerRefusal> =>
  failure instanceof OnboardingRequestRefused ? failure.refusal : Option.none();

/**
 * Whether Power answered that what an onboarding read asked for does not exist (404): reading it
 * again cannot succeed. A network failure or any other refusal is not.
 */
export const onboardingRefusedNotFound = (failure: Error): boolean =>
  failure instanceof OnboardingRequestRefused && failure.status === HTTP_NOT_FOUND;

/**
 * What the screen says about a failed onboarding read: Power's own words when it refused it (the
 * Problem's detail, or the status in pt-BR), otherwise a pt-BR sentence, so a network `TypeError`
 * or a decode error never reaches the operator in English.
 */
export const onboardingFailureText = (failure: Error): string =>
  failure instanceof OnboardingRequestRefused
    ? failure.message
    : 'O Power não respondeu ou respondeu fora do contrato.';
